import { Controller } from "@hotwired/stimulus"
import { DebounceHelper } from "~/helpers/debounce_helper"
import { AttachmentEventHelper } from "~/helpers/attachment_event_helper"

export default class extends Controller {
  static get targets() {
    return [
      "bodyField",
      "form",
      "nameField",
      "subjectField",
      "subjectHintIcon",
      "sendTestButton",
      "trixEditor",
      "saveLoader",
      "emailIcon",
      "saveMessage",
      "errorMessage",
      "errorMessageIcon",
      "errorMessageContainer",
      "templateSelect",
      "waitDaysSelect",
      "bodyContent",
      "spamButton",
      "messageFormatField",
    ]
  }

  static get values() {
    return { id: Number, followUpId: Number, started: Boolean, step: Number, justSaved: Boolean }
  }

  connect() {
    var _this = this

    // Save the campaign content when the subject changes
    this.subjectFieldTarget.addEventListener("keyup", this.debounceSaveCampaignContentFunction())
    this.subjectFieldTarget.addEventListener("keyup", () => {
      this.dispatch("saving-follow-up", { detail: { id: this.idValue, step: this.stepValue } })
    })

    // Flag to detect real user interaction
    this.userInteracted = false

    // Setup attachment and user interaction events using shared helper
    this.attachmentEventCleanup = AttachmentEventHelper.setupAllEvents(
      this.trixEditorTarget,
      () => {
        this.userInteracted = true
      },
      () => {
        // Trigger save for attachment changes (since they might not fire trix-change)
        this.debounceSaveCampaignContentFunction()()
        this.dispatch("saving-follow-up", { detail: { id: this.idValue, step: this.stepValue } })
      },
    )

    // Save the campaign content when the body changes – only after user interaction
    this.trixEditorTarget.addEventListener("trix-change", () => {
      if (!this.userInteracted) {
        return
      }

      this.debounceSaveCampaignContentFunction()()
      this.dispatch("saving-follow-up", { detail: { id: this.idValue, step: this.stepValue } })
    })

    if (this.justSavedValue) {
      this.showSaveMessage()
    }

    // The followup is saved right away to ensure prevent edge case bugs.
    if (this.startedValue === false) {
      this.saveCampaignContent(false)
    }

    // Make the Ctrl + S or Command + S save the emails
    document.addEventListener("keydown", (e) => {
      if ((window.navigator.platform.match("Mac") ? e.metaKey : e.ctrlKey) && e.key === "s") {
        e.preventDefault()
        this.saveCampaignContent()
      }
    })

    if (_this.hasSubjectHintIconTarget) {
      // Close the popover about subject line hints when anything else is clicked.
      $("body").on("click", function (e) {
        if (e.target != _this.subjectHintIconTarget && !$(e.target).parents(".popover-content").length) {
          App.popover(_this.subjectHintIconTarget, "dispose")
        }
      })
    }

    // Because the message editor for a follow up can be reloaded through a Turbo stream we should check if the
    // next button should be toggled on connect
    this.application
      .getControllerForElementAndIdentifier(
        document.getElementById("campaign-content-container"),
        "campaign--content-next-button",
      )
      .toggle()
  }

  disconnect() {
    if (this.debounceSaveCampaignContent) {
      this.debounceSaveCampaignContent.cancel()
    }

    // Cleanup attachment event listeners to prevent memory leaks
    if (this.attachmentEventCleanup) {
      this.attachmentEventCleanup.cleanup()
    }
  }

  debounceSaveCampaignContentFunction() {
    // Cache result so we can cancel debounce function in disconnect()
    if (this.debounceSaveCampaignContent) {
      return this.debounceSaveCampaignContent
    }

    // If the campaign is started, we might update all the pending messages
    // every time the content changes. So it's safer to use a greater timeout
    // in that case.
    const debounceTimeout = this.startedValue ? 3000 : 1500

    this.debounceSaveCampaignContent = DebounceHelper.debounce(() => {
      this.saveCampaignContent()
    }, debounceTimeout)
    return this.debounceSaveCampaignContent
  }

  // This is a shortcut to the saveCampaignContent method, to match the method name in message_controller.js
  // This is used when clicking the HTML toggle
  save() {
    this.saveCampaignContent(true, null, null, true)
  }

  saveCampaignContent(withMessage = true, subject = null, body = null, reload = null) {
    // For unknown reasons, the campaign is sometimes saved on external
    // pages. This guard clause prevents this to happen.
    if ($(this.formTarget).length == 0) {
      return
    }

    console.info("Updating campaign content for step " + this.stepValue)

    this.disableButton(this.sendTestButtonTarget)

    if (this.hasSpamButtonTarget) {
      this.disableButton(this.spamButtonTarget)
    }

    if (withMessage) {
      this.showLoader()
    }

    // The default selected value is 1 wait day. But for the first follow-up,
    // there's no wait day so we force this value to 0.
    var followUpWaitDays = this.hasWaitDaysSelectTarget && this.stepValue != 0 ? this.waitDaysSelectTarget.value : 0

    // Removes the <code> tag from the content before saving it in the database
    // as it's just a visual element
    const sanitizedBody = this.bodyFieldTarget.value.replace(/<code>|<\/code>/gm, ``)

    var formData = [
      { name: "follow_up[id]", value: this.followUpIdValue },
      { name: "follow_up[step]", value: this.stepValue },
      { name: "follow_up[subject]", value: subject !== null ? subject : this.subjectFieldTarget.value },
      { name: "follow_up[body]", value: body !== null ? body : sanitizedBody },
      { name: "follow_up[wait_days]", value: followUpWaitDays },
    ]

    if (this.hasMessageFormatFieldTarget) {
      formData.push({ name: "follow_up[message_format]", value: this.messageFormatFieldTarget.value })
    }

    if (reload || body !== null || subject !== null) {
      formData.push({ name: "reload", value: "1" })
    }

    $.ajax({
      url: `/campaigns/${this.idValue}/follow-ups/${this.stepValue}`,
      credentials: "same-origin",
      headers: {
        "Email-Hunter-Origin": "website_dashboard",
        "X-CSRF-Token": $("meta[name='csrf-token']").attr("content"),
      },
      type: "PUT",
      data: formData,
      dataType: "json",
      error: (err) => {
        this.dispatch("finished-saving-follow-up", { detail: { id: this.idValue, step: this.stepValue } })
        this.showErrorMessage(err.responseJSON.error)
        console.warn("The content couldn't be updated.")
      },
      success: () => {
        // Update the "raw content" with new content
        // this "raw" content is used to display custom attribute as tag in content
        if (this.hasBodyContentTarget) {
          this.bodyContentTarget.value = this.bodyFieldTarget.value
        }

        // A value is required only for the subject of the first message, follow-ups subject
        // lines can be empty.
        if (this.bodyFieldTarget.value != "" && (this.stepValue > 0 || this.subjectFieldTarget.value != "")) {
          this.enableButton(this.sendTestButtonTarget)
        }

        if (this.bodyFieldTarget.value != "" && this.hasSpamButtonTarget) {
          this.enableButton(this.spamButtonTarget)
        }

        if (withMessage) {
          this.showSaveMessage()
        }

        this.dispatch("finished-saving-follow-up", { detail: { id: this.idValue, step: this.stepValue } })
      },
    })
  }

  showLoader() {
    if (this.hasEmailIconTarget) {
      this.saveLoaderTarget.classList.remove("d-none")
      this.emailIconTarget.classList.add("d-none")
      this.errorMessageIconTarget.classList.add("d-none")
      this.errorMessageContainerTarget.classList.add("d-none")
      this.saveMessageTarget.classList.add("d-none")
    } else {
      if (this.hasSaveMessageTarget) {
        $(this.saveMessageTarget).hide()
      }

      if (this.hasErrorMessageTarget) {
        $(this.errorMessageTarget).hide()
      }

      if (this.hasSaveLoaderTarget) {
        $(this.saveLoaderTarget).show()
      }
    }
  }

  showSaveMessage() {
    if (this.hasEmailIconTarget) {
      this.emailIconTarget.classList.add("d-none")
      this.saveLoaderTarget.classList.add("d-none")
      this.errorMessageIconTarget.classList.add("d-none")
      this.errorMessageContainerTarget.classList.add("d-none")
      this.saveMessageTarget.classList.remove("d-none")

      setTimeout(() => {
        this.saveMessageTarget.classList.add("d-none")
        this.emailIconTarget.classList.remove("d-none")
      }, 2000)
    } else {
      if (this.hasSaveMessageTarget) {
        $(this.errorMessageTarget).hide()
      }

      if (this.hasSaveLoaderTarget) {
        $(this.saveLoaderTarget).hide()
      }

      if (this.hasSaveMessageTarget) {
        $(this.saveMessageTarget).show()
      }
    }
  }

  showErrorMessage(details) {
    if (this.hasEmailIconTarget) {
      this.emailIconTarget.classList.add("d-none")
      this.saveLoaderTarget.classList.add("d-none")
      this.saveMessageTarget.classList.add("d-none")
      this.errorMessageContainerTarget.textContent = details
      this.errorMessageContainerTarget.classList.remove("d-none")
      this.errorMessageIconTarget.classList.remove("d-none")

      setTimeout(() => {
        this.errorMessageIconTarget.classList.add("d-none")
        this.errorMessageContainerTarget.classList.add("d-none")
        this.emailIconTarget.classList.remove("d-none")
      }, 3000)
    } else {
      if (this.hasSaveMessageTarget) {
        $(this.saveMessageTarget).hide()
      }

      if (this.hasSaveLoaderTarget) {
        $(this.saveLoaderTarget).hide()
      }

      // Reset the default error message and show it
      $(this.errorMessageTarget).html(this.errorMessageTarget.dataset.defaultValue)

      $(this.errorMessageTarget).show()

      // If there are some details coming with the error message, display them in a tooltip
      if (typeof details !== "undefined") {
        let tooltip = document.createElement("span")
        tooltip.setAttribute("data-toggle", "tooltip")
        tooltip.setAttribute("data-placement", "top")
        tooltip.setAttribute("title", details)
        tooltip.classList.add("far", "fa-question-circle")
        tooltip.style.marginLeft = "5px"

        this.errorMessageTarget.append(tooltip)

        App.tooltip(tooltip)
      }
    }
  }

  disableButton(button) {
    $(button).addClass("disabled")
  }

  enableButton(button) {
    $(button).removeClass("disabled")
  }

  // templateWasSelected is triggered when a template was selected. The subject
  // and body are then replaced using the selected template.
  templateWasSelected() {
    console.info("A template was selected: We will update the subject and body")

    const templateSelect = this.templateSelectTarget

    // If the user selects the default option, we return.
    if (templateSelect.selectedIndex == 0) {
      return
    }

    const selectedTemplate = templateSelect.options[templateSelect.selectedIndex]

    // Apply the message_format from the template
    if (this.hasMessageFormatFieldTarget && selectedTemplate.dataset["messageFormat"]) {
      this.messageFormatFieldTarget.value = selectedTemplate.dataset["messageFormat"]

      // Toggle HTML mode in trix-editor based on message_format
      const trixEditorElement = this.element.querySelector('[data-controller~="trix-editor"]')
      if (trixEditorElement) {
        const trixEditorController = this.application.getControllerForElementAndIdentifier(
          trixEditorElement,
          "trix-editor",
        )

        if (trixEditorController) {
          if (selectedTemplate.dataset["messageFormat"] === "html") {
            trixEditorController.enableHtml()
          } else if (selectedTemplate.dataset["messageFormat"] === "text") {
            trixEditorController.disableHtml(true)
          }
        }
      }
    }

    this.saveCampaignContent(true, selectedTemplate.dataset["subject"], selectedTemplate.dataset["body"])
  }

  deleteFollowUp(e) {
    if (this.stepValue == 0) {
      App.displayFlash("danger", i18n.t("js.back.campaigns.follow_up.delete_first_follow_up_error"))
      return
    }

    fetch(`/campaigns/${this.idValue}/follow-ups/${this.stepValue}`, {
      credentials: "same-origin",
      method: "DELETE",
      headers: {
        "X-CSRF-Token": $("meta[name='csrf-token']").attr("content"),
      },
    })
      .then((response) => {
        // Cancel the debounce function to prevent the saveCampaignContent from being called after the follow-up is deleted
        this.debounceSaveCampaignContent.cancel()

        if (response.ok) {
          const followUpContainer = e.target.closest(".compose-follow-up")

          if (followUpContainer) {
            followUpContainer.remove()
          }

          this.application
            .getControllerForElementAndIdentifier(
              document.getElementById("campaign-content-container"),
              "campaign--content-next-button",
            )
            .toggle()

          App.displayFlash("success", i18n.t("js.back.campaigns.follow_up.delete_success"))
          return
        }

        return response.json()
      })
      .then((json) => {
        if (json !== undefined) {
          App.displayFlash("danger", json.error)
        }
      })
  }

  showHints(event) {
    let hints = document.createElement("div")
    hints.innerHTML = $(event.target).data("hints")

    let customHintsLeft = $(hints).find(".custom-hint").length

    if (this.subjectFieldTarget.value.length < 50) {
      $(hints).find("#subject-line-too-long").remove()
      customHintsLeft--
    }

    if (this.subjectFieldTarget.value.length > 5) {
      $(hints).find("#subject-line-too-short").remove()
      customHintsLeft--
    }

    if (this.subjectFieldTarget.value.includes("{{") && this.subjectFieldTarget.value.includes("}}")) {
      $(hints).find("#custom-attribute-missing").remove()
      customHintsLeft--
    }

    if (customHintsLeft === 0) {
      $(hints).find("#custom-hints-container").remove()
    }

    App.popover(event.target, { html: true, placement: "top", container: "body", content: hints })
  }
}
